import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseStatsRange, STATS_PARAMS } from '@/config/httpStats';
import type { StatsRange } from '@/types/stats';

/**
 * Statistics page state kept in the URL (range, node filter, open domain),
 * so a view can be shared and the dashboard can link straight to a site.
 */
export function useStatsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = parseStatsRange(searchParams.get(STATS_PARAMS.range));
  const nodeId = searchParams.get(STATS_PARAMS.node) ?? '';
  const domain = searchParams.get(STATS_PARAMS.domain) || null;

  /**
   * Set (or with '' remove) parameters; filter changes replace the history
   * entry instead of piling up
   */
  const update = useCallback(
    (changes: Partial<Record<keyof typeof STATS_PARAMS, string>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            const name = STATS_PARAMS[key as keyof typeof STATS_PARAMS];
            if (value) next.set(name, value);
            else next.delete(name);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setRange = useCallback((value: StatsRange) => update({ range: value }), [update]);
  const setNodeId = useCallback((value: string) => update({ node: value }), [update]);
  const closeDomain = useCallback(() => update({ domain: '' }), [update]);

  /**
   * Link (search string) that opens a domain's drill-down with the current filters
   */
  const getDomainHref = useCallback(
    (name: string) => {
      const next = new URLSearchParams(searchParams);
      next.set(STATS_PARAMS.domain, name);
      return `?${next.toString()}`;
    },
    [searchParams]
  );

  return { range, nodeId, domain, setRange, setNodeId, closeDomain, getDomainHref };
}
